import { useEffect, useState } from 'react';
import { Layout, Result, ConfigProvider } from 'antd';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import routes from '@root/routes';
import { userType } from '@propTypes';
import { setBugsnagUser } from '@shared/bugsnagClient';
import useUserRegisterer from '@components/shared/useUserRegisterer';
import api from '@shared/api';
import '@styles/tailwind.css';

import BuildingPage from './BuildingPage';
import Navigation from './Navigation';
import PropertySearch from './PropertySearch';
import PropertyPage from './PropertyPage';
import UserSearch from './UserSearch';
import UserPage from './UserPage';
import EditListing from './EditListing';
import ListingSearch from './ListingSearch';
import BuildingSearch from './BuildingSearch';
import SidebarMenu from './SidebarMenu';
import Companies from './Companies';
import Landlords from './Landlords';
import TourbookExportTool from './TourbookExportTool';
import s from './App.module.less';

const { Header, Content, Sider } = Layout;
const {
  admin: {
    root: adminRootRoute,
    vtsPropertySearch: vtsPropertySearchRoute,
    vtsProperty: vtsPropertyRoute,
    building: buildingRoute,
    buildingSearch: buildingSearchRoute,
    userSearch: userSearchRoute,
    userPage: userPageRoute,
    listingPage: listingPageRoute,
    listingSearch: listingSearchRoute,
    companies: companiesPageRoutes,
    landlords: landlordsPageRoutes,
  },
} = routes;

Modal.setAppElement('#layouts-admin');

function App({ currentUser }) {
  const [requestedListingsCount, setRequestedListingsCount] = useState(0);

  useEffect(() => {
    fetchRequestedListings();
  }, []);

  const fetchRequestedListings = async () => {
    const response = await api.fetch(
      routes.api.admin.listingSearch({
        statuses: ['requested'],
      }),
    );
    const { pagination } = await response.json();

    setRequestedListingsCount(pagination.totalCount);
  };

  useUserRegisterer();
  if (!currentUser) return null;
  setBugsnagUser(currentUser);

  return (
    /*
      ConfigProvider with getPopupContainer was added to fix popup components,
      such as Dropdown and Select in scrollable containers. Prior to this fix,
      dropdowns would remain in a fixed position on the screen when scrolling.
      See details here:
      https://ant.design/docs/react/faq
      #Select-Dropdown-DatePicker-TimePicker-Popover-Popconfirm-scrolls-with-the-page
    */
    <ConfigProvider getPopupContainer={trigger => (trigger ? trigger.parentNode : document.body)}>
      <Layout style={{ height: '100%' }}>
        <Header className={s.header}>
          <Navigation currentUser={currentUser} />
        </Header>
        <Layout>
          <Sider width={200} style={{ background: 'white' }}>
            <SidebarMenu requestedListingsCount={requestedListingsCount} />
          </Sider>
          <Layout>
            <Content style={{ padding: '20px', height: '100%' }}>
              <Switch>
                <Route path={vtsPropertyRoute(':id')} component={PropertyPage} />
                <Route path={vtsPropertySearchRoute} component={PropertySearch} />
                <Route path={buildingRoute(':id')} component={BuildingPage} />
                <Route path={buildingSearchRoute} component={BuildingSearch} />
                <Route path={userPageRoute(':id')} component={UserPage} />
                <Route path={userSearchRoute} component={UserSearch} />
                <Route
                  path={listingPageRoute(':id')}
                  render={props => (
                    <EditListing
                      {...props}
                      refreshRequestedListingsCount={fetchRequestedListings}
                    />
                  )}
                />
                <Route
                  path={listingSearchRoute}
                  render={props => (
                    <ListingSearch
                      {...props}
                      requestedListingsCount={requestedListingsCount}
                      refreshRequestedListingsCount={fetchRequestedListings}
                    />
                  )}
                />
                <Route path={companiesPageRoutes} component={Companies} />
                <Route path={landlordsPageRoutes} component={Landlords} />
                <Route path={routes.admin.tourbookExportTool} component={TourbookExportTool} />
                <Route
                  path={adminRootRoute}
                  exact
                  render={() => <Redirect to={buildingSearchRoute} />}
                />
                <Route component={() => <Result status="404" title="404" />} />
              </Switch>
            </Content>
          </Layout>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}

App.defaultProps = {
  currentUser: null,
};

App.propTypes = {
  currentUser: userType,
};

const mapStateToProps = ({ currentUser }) => ({
  currentUser,
});

export default connect(mapStateToProps)(App);
